import { AemItemsKey, AemOrderKey, ExperienceFragmentResponse } from '../types';

const PARAGRAPH_REGEX = /<p[^>]*>([\s\S]*?)<\/p>/gi;

const HTML_ENTITY_MAP: Record<string, string> = {
  amp: '&',
  lt: '<',
  gt: '>',
  apos: "'",
  quot: '"',
  nbsp: ' ',
  ndash: '–',
  mdash: '—',
  rsquo: '’',
  lsquo: '‘',
  ldquo: '“',
  rdquo: '”',
};

function extractTextNode(fragment: ExperienceFragmentResponse): string {
  const items = fragment[AemItemsKey]?.root;
  const order = items?.[AemOrderKey];
  if (!items || !order || order.length === 0) {
    throw new Error('EULA text not found in experience fragment');
  }
  const firstKey = order[0];
  const textNode = items[AemItemsKey]?.[firstKey]?.text;
  if (!textNode) {
    throw new Error('EULA text not found in experience fragment');
  }
  return textNode;
}

function cleanParagraph(html: string): string {
  const replacedBreaks = html.replace(/<br\s*\/?>/gi, '\n');
  const withoutTags = replacedBreaks.replace(/<[^>]+>/g, ' ');
  const decoded = decodeEntities(withoutTags);
  const normalizedWhitespace = decoded
    .replace(/\r\n|\r/g, '\n')
    .split('\n')
    .map((line) => line.replace(/\s+/g, ' ').trim())
    .filter(Boolean)
    .join(' ');

  return normalizedWhitespace
    .replace(/\s+([,.;:!?])/g, '$1')
    .replace(/\(\s+/g, '(')
    .replace(/\s+\)/g, ')')
    .trim();
}

function decodeEntities(value: string): string {
  return value.replace(/&(#(?:x)?[\da-f]+|\w+);/gi, (match, entity) => {
    const lower = entity.toLowerCase();
    if (lower.startsWith('#x')) {
      return String.fromCharCode(parseInt(lower.slice(2), 16));
    }
    if (lower.startsWith('#')) {
      return String.fromCharCode(parseInt(lower.slice(1), 10));
    }
    return HTML_ENTITY_MAP[lower] ?? match;
  });
}

export default function extractParagraphs(raw: ExperienceFragmentResponse): string[] {
  const html = extractTextNode(raw);
  const paragraphs: string[] = [];

  for (const match of html.matchAll(PARAGRAPH_REGEX)) {
    const inner = match[1]?.trim();
    if (!inner) continue;
    const cleaned = cleanParagraph(inner);
    if (cleaned) {
      paragraphs.push(cleaned);
    }
  }

  return paragraphs;
}
